/**
 * YIN-based Pitch Detection Engine
 * Optimized for real-time vocal pitch detection in AudioWorklet
 */

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export interface PitchResult {
    frequency: number;     // Hz
    confidence: number;    // 0.0 ~ 1.0
    midi: number;          // MIDI note number
    noteName: string;      // e.g., "C4"
    noteNumber: number;    // note within octave (0-11)
    octave: number;
    cents: number;         // cents deviation from nearest note
}

export function hzToMidi(hz: number): number {
    if (hz <= 0) return 0;
    return 12 * Math.log2(hz / 440) + 69;
}

export function midiToHz(midi: number): number {
    return 440 * Math.pow(2, (midi - 69) / 12);
}

export function midiToNoteName(midi: number): string {
    if (midi <= 0) return '';
    const noteNum = Math.round(midi) % 12;
    const octave = Math.floor(Math.round(midi) / 12) - 1;
    return `${NOTE_NAMES[noteNum]}${octave}`;
}

export function hzToNote(hz: number): PitchResult {
    if (hz <= 0) {
        return { frequency: 0, confidence: 0, midi: 0, noteName: '', noteNumber: 0, octave: 0, cents: 0 };
    }
    const midi = hzToMidi(hz);
    const roundedMidi = Math.round(midi);
    const cents = Math.round((midi - roundedMidi) * 100);
    const noteNumber = roundedMidi % 12;
    const octave = Math.floor(roundedMidi / 12) - 1;
    return {
        frequency: hz,
        confidence: 1,
        midi: roundedMidi,
        noteName: `${NOTE_NAMES[noteNumber >= 0 ? noteNumber : 0]}${octave}`,
        noteNumber: noteNumber >= 0 ? noteNumber : 0,
        octave,
        cents
    };
}

/**
 * YIN pitch detection algorithm
 */
export function detectPitchYIN(buffer: Float32Array, sampleRate: number, threshold: number = 0.15): PitchResult | null {
    const bufferSize = buffer.length;
    const halfSize = Math.floor(bufferSize / 2);

    // Step 1: Calculate RMS to check if there's enough signal
    let rms = 0;
    for (let i = 0; i < bufferSize; i++) {
        rms += buffer[i] * buffer[i];
    }
    rms = Math.sqrt(rms / bufferSize);
    if (rms < 0.01) return null; // Too quiet

    // Step 2: Difference function
    const yinBuffer = new Float32Array(halfSize);
    for (let tau = 0; tau < halfSize; tau++) {
        let sum = 0;
        for (let i = 0; i < halfSize; i++) {
            const delta = buffer[i] - buffer[i + tau];
            sum += delta * delta;
        }
        yinBuffer[tau] = sum;
    }

    // Step 3: Cumulative mean normalized difference
    yinBuffer[0] = 1;
    let runningSum = 0;
    for (let tau = 1; tau < halfSize; tau++) {
        runningSum += yinBuffer[tau];
        yinBuffer[tau] = yinBuffer[tau] * tau / runningSum;
    }

    // Step 4: Absolute threshold
    let tauEstimate = -1;
    for (let tau = 2; tau < halfSize; tau++) {
        if (yinBuffer[tau] < threshold) {
            while (tau + 1 < halfSize && yinBuffer[tau + 1] < yinBuffer[tau]) {
                tau++;
            }
            tauEstimate = tau;
            break;
        }
    }

    if (tauEstimate === -1) return null;

    // Step 5: Parabolic interpolation
    let betterTau: number;
    const x0 = tauEstimate < 1 ? tauEstimate : tauEstimate - 1;
    const x2 = tauEstimate + 1 < halfSize ? tauEstimate + 1 : tauEstimate;

    if (x0 === tauEstimate) {
        betterTau = yinBuffer[tauEstimate] <= yinBuffer[x2] ? tauEstimate : x2;
    } else if (x2 === tauEstimate) {
        betterTau = yinBuffer[tauEstimate] <= yinBuffer[x0] ? tauEstimate : x0;
    } else {
        const s0 = yinBuffer[x0];
        const s1 = yinBuffer[tauEstimate];
        const s2 = yinBuffer[x2];
        betterTau = tauEstimate + (s2 - s0) / (2 * (2 * s1 - s2 - s0));
    }

    const frequency = sampleRate / betterTau;
    const confidence = 1 - yinBuffer[tauEstimate];

    // Filter out unreasonable frequencies (human voice range: ~60Hz - 1500Hz)
    if (frequency < 60 || frequency > 1500) return null;

    const result = hzToNote(frequency);
    result.confidence = Math.max(0, Math.min(1, confidence));
    return result;
}

/**
 * Calculates RMS (Root Mean Square) of audio buffer
 */
export function calculateRMS(buffer: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
        sum += buffer[i] * buffer[i];
    }
    return Math.sqrt(sum / buffer.length);
}

/**
 * Check for audio clipping
 */
export function detectClipping(buffer: Float32Array, threshold: number = 0.99): boolean {
    for (let i = 0; i < buffer.length; i++) {
        if (Math.abs(buffer[i]) >= threshold) return true;
    }
    return false;
}
